import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { number } from 'prop-types'
import ScrollAnimation from 'react-animate-on-scroll'

import { media } from 'styles/media'
import { square, animationTime, animationCurve } from 'styles/global'
import { white } from 'styles/colors'
import FeaturedProduct from 'components/home/FeaturedProduct'
import FlavourShowcase from 'components/home/FlavourShowcase'
import FlavourCarousel from 'components/home/FlavourShowcase/FlavourCarousel'
import CustomizeCarousel from 'components/home/FlavourShowcase/CustomizeCarousel'
import CustomizeEpod from 'components/home/CustomizeEpod'
import * as spacing from 'styles/spacing'

const Container = styled.div`
  position: relative;
  overflow: hidden;
`

const Inner = styled.div`
  display: flex;
  height: 700px;
  width: ${props => `${100 * props.steps}vw`};
  transition: transform 0.5s cubic-bezier(0.455, 0.03, 0.515, 0.955);
  transform: translateX(${props => `${(-100 / props.steps) * props.step}%`});

  ${media.small`
    height: 782px;
  `};

  ${media.medium`
    height: 760px;
    transition-duration: 1s;
  `};

  ${media.large`
    height: 860px;
  `};

  ${media.xLarge`
    height: 900px;
  `};

  ${media.xxLarge`
    height: 1100px;
  `};
`

const Slide = styled.div`
  height: 100%;
  width: 100vw;
  position: relative;
`

const Wipe = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 40;
  background-color: #6f2473;
  transition: transform 1s cubic-bezier(0.455, 0.03, 0.515, 0.955);
  transition-delay: 0.4s;

  ${props =>
    props.step === 1 &&
    css`
      transform: translateX(-100%);
    `}
`

const Dots = styled.div`
  display: flex;
  justify-content: center;
  padding-top: ${spacing.large};
  padding-bottom: ${spacing.xLarge};

  ${media.small`
    padding-top: ${spacing.large};
    padding-bottom: ${spacing.xLarge};
  `};

  ${media.medium`
    padding-top: ${spacing.large};
    padding-bottom: ${spacing.xxLarge};
  `};

  ${media.large`
    padding-top: ${spacing.xLarge};
    padding-bottom: ${spacing.xxxLarge};
  `};
`

const Dot = styled.div`
  background-color: ${white};
  border-radius: 50%;
  opacity: 0.3;
  cursor: pointer;
  transition: opacity ${animationTime} ${animationCurve};
  ${square('10px')};

  &:hover {
    opacity: 0.5;
  }

  & + & {
    margin-left: ${spacing.small};
  }

  ${props =>
    props.active &&
    css`
      opacity: 1;

      &:hover {
        opacity: 1;
      }
    `}
`

const initialEpodFlavours = [
  {
    name: 'Watermelon',
    prettyName: 'Watermelon',
    description: 'A watermelon flavour, with a blend of subtle green notes.',
    link: '/buy-online/vype-epod-vpro-cartridges/watermelon',
    price: '$13.00',
    slug: 'watermelon',
    visible: true,
    background: 'linear-gradient(116.87deg, #D14441 -7.61%, #F19285 115.55%), #F2968A',
    isNew: true
  },
  {
    name: 'Blueberry',
    prettyName: 'Blueberry',
    description: 'A blueberry flavour with undertones of red berries and tartness.',
    link: '/buy-online/vype-epod-vpro-cartridges/blueberry',
    price: '$13.00',
    slug: 'blueberry',
    visible: false,
    background: 'linear-gradient(116.87deg, #A296C9 -7.61%, #4D4384 113.2%), #4F4485',
    isNew: true
  },
  {
    name: 'Lychee',
    prettyName: 'Lychee',
    description: 'A combination of lychee and mango notes',
    link: '/buy-online/vype-epod-vpro-cartridges/lychee-orchard',
    price: '$13.00',
    slug: 'lychee',
    visible: true,
    background: 'linear-gradient(110.85deg, #C13930 -7.61%, #B33C36 115.55%), #DE4448',
    isOnlineOnly: true
  },
  {
    name: 'Passionfruit',
    prettyName: 'Passionfruit',
    description: 'Tropical passionfruit with almond notes',
    link: '/buy-online/vype-epod-vpro-cartridges/passionfruit-fields',
    price: '$13.00',
    slug: 'passionfruit',
    visible: true,
    background: 'linear-gradient(110.85deg, #7B2783 -7.61%, #551E53 115.55%), #A55199',
    isOnlineOnly: true
  },
  {
    name: 'Blood Orange',
    prettyName: 'Blood<br/> Orange',
    description: 'An orange flavour with red pomegranate notes',
    link: '/buy-online/vype-epod-vpro-cartridges/blood-orange-epod-cartridge',
    price: '$13.00',
    slug: 'blood-orange',
    visible: true,
    background: 'linear-gradient(110.85deg, #EC6A4C -7.61%, #B33C36 115.55%), #E5995B'
  },
  {
    name: 'Cucumber',
    prettyName: 'Cucumber',
    description: 'Cucumber freshness with an element of tanginess',
    link: '/buy-online/vype-epod-vpro-cartridges/infused-cucumber-epod-cartridge',
    price: '$13.00',
    slug: 'cucumber',
    visible: true,
    background: 'linear-gradient(110.85deg, #589B75 -7.61%, #023A1A 113.2%), #67A481'
  },
  {
    name: 'Golden Tobacco',
    prettyName: 'Golden<br/> Tobacco',
    description: 'A  well balanced Intensity with a smooth taste',
    link: '/buy-online/vype-epod-vpro-cartridges/epod-nicotine-salts-tobacco-marvel-vpro-cartridges',
    price: '$13.00',
    slug: 'tobacco',
    visible: true,
    background: 'linear-gradient(110.85deg, #D18F0E -7.61%, #543000 115.55%), #B47908'
  },
  {
    name: 'Polar Mint',
    prettyName: 'Polar<br/> Mint',
    description: 'A deep cooling spearmint and peppermint flavour',
    link: '/buy-online/vype-epod-vpro-cartridges/epod-vpro-cartridges-polar-mint',
    price: '$13.00',
    slug: 'mint',
    visible: true,
    background: 'linear-gradient(110.85deg, #5D9892 -7.61%, #0B3C37 115.55%), #073833'
  },
  {
    name: 'Vanilla',
    prettyName: 'Vanilla',
    description: 'A rich vanilla taste with a smooth texture',
    link: '/buy-online/vype-epod-vpro-cartridges/epod-nicotine-salts-vanilla-medley',
    price: '$13.00',
    slug: 'vanilla',
    visible: true,
    background: 'linear-gradient(110.85deg, #CA9957 -7.61%, #725628 113.2%), #9B7438'
  },
  {
    name: 'Strawberry',
    prettyName: 'Strawberry',
    description: 'A fusion of strawberry with green fruit notes',
    link: '/buy-online/vype-epod-vpro-cartridges/garden-strawberry',
    price: '$13.00',
    slug: 'strawberry',
    visible: true,
    background: 'linear-gradient(110.85deg, #DF525C -7.61%, #A4101C 115.55%), #DD0C34'
  }
]

const EpodSlideshow = ({ steps }) => {
  const [step, setStep] = useState(0)
  const [epodFlavours, setEpodFlavours] = useState(initialEpodFlavours)

  const hideCurrentFlavour = slug => {
    const upToDateEpodFlavours = [...epodFlavours]
    epodFlavours.forEach((flavour, j) => {
      slug === flavour.slug ? (upToDateEpodFlavours[j].visible = false) : (upToDateEpodFlavours[j].visible = true)
    })
    setEpodFlavours(upToDateEpodFlavours)
  }

  return (
    <Container>
      <Inner step={step} steps={steps}>
        <Slide>
          <FeaturedProduct
            link="/vype-epod-starter-kit/"
            heading="ePod"
            subheading="8 COLOURS | 17 FLAVOURS"
            price="$9.99"
            cta="EXPLORE FLAVOURS"
            handleCtaClick={() => setStep(1)}
            background="https://images.unsplash.com/photo-1518893063132-36e46dbe2428?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1275&q=80"
            device="epod"
            gradient="linear-gradient(206.09deg, #9F827D -76.74%, #CF9289 153.46%)"
            accentColor="#8e5c58"
            navigation={[
              {
                label: 'FLAVOURS',
                icon: 'pods',
                onClick: () => setStep(1)
              },
              {
                label: 'PERSONALIZE',
                icon: 'personalize',
                onClick: () => setStep(2)
              }
            ]}
          />
        </Slide>
        <Slide>
          <Wipe step={step} className="u-hide-medium-down" />
          <FlavourCarousel
            navigation={[
              {
                label: 'ePod',
                icon: 'epod',
                onClick: () => setStep(0)
              },
              {
                label: 'PERSONALIZE',
                icon: 'personalize',
                onClick: () => setStep(2)
              }
            ]}
            flavours={[
              {
                name: 'Watermelon',
                prettyName: 'Watermelon',
                description: 'A watermelon flavour, with a blend of subtle green notes.',
                link: '/buy-online/vype-epod-vpro-cartridges/watermelon/',
                price: '$13.00',
                slug: 'watermelon',
                background: 'linear-gradient(116.87deg, #D14441 -7.61%, #F19285 115.55%), #F2968A',
                isNew: true
              },
              {
                name: 'Blueberry',
                prettyName: 'Blueberry',
                description: 'A blueberry flavour with undertones of red berries and tartness.',
                link: '/buy-online/vype-epod-vpro-cartridges/blueberry/',
                price: '$13.00',
                slug: 'blueberry',
                background: 'linear-gradient(116.87deg, #A296C9 -7.61%, #4D4384 113.2%), #4F4485',
                isNew: true
              },
              {
                name: 'Lychee',
                prettyName: 'Lychee',
                description: 'A combination of lychee and mango notes.',
                link: '/buy-online/vype-epod-vpro-cartridges/lychee-orchard/',
                price: '$13.00',
                slug: 'lychee',
                background: 'linear-gradient(110.85deg, #C13930 -7.61%, #B33C36 115.55%), #DE4448',
                isOnlineOnly: true
              },
              {
                name: 'Passionfruit',
                prettyName: 'Passionfruit',
                description: 'Tropical passionfruit with almond notes.',
                link: '/buy-online/vype-epod-vpro-cartridges/passionfruit-fields/',
                price: '$13.00',
                slug: 'passionfruit',
                background: 'linear-gradient(110.85deg, #7B2783 -7.61%, #551E53 115.55%), #A55199',
                isOnlineOnly: true
              },
              {
                name: 'Blood Orange',
                prettyName: 'Blood<br/> Orange',
                description: 'An orange flavour with red pomegranate notes.',
                link: '/buy-online/vype-epod-vpro-cartridges/blood-orange-epod-cartridge/',
                price: '$13.00',
                slug: 'blood-orange',
                background: 'linear-gradient(110.85deg, #EC6A4C -7.61%, #B33C36 115.55%), #E5995B'
              },
              {
                name: 'Cucumber',
                prettyName: 'Cucumber',
                description: 'Cucumber freshness with an element of tanginess.',
                link: '/buy-online/vype-epod-vpro-cartridges/infused-cucumber-epod-cartridge/',
                price: '$13.00',
                slug: 'cucumber',
                background: 'linear-gradient(110.85deg, #589B75 -7.61%, #023A1A 113.2%), #67A481'
              },
              {
                name: 'Golden Tobacco',
                prettyName: 'Golden <br />Tobacco',
                description: 'A  well balanced Intensity with a smooth taste.',
                link: '/buy-online/vype-epod-vpro-cartridges/epod-nicotine-salts-tobacco-marvel-vpro-cartridges/',
                price: '$13.00',
                slug: 'tobacco',
                background: 'linear-gradient(110.85deg, #D18F0E -7.61%, #543000 115.55%), #B47908'
              },
              {
                name: 'Polar Mint',
                prettyName: 'Polar<br/> Mint',
                description: 'A deep cooling spearmint and peppermint flavour.',
                link: '/buy-online/vype-epod-vpro-cartridges/epod-vpro-cartridges-polar-mint/',
                price: '$13.00',
                slug: 'mint',
                background: 'linear-gradient(110.85deg, #5D9892 -7.61%, #0B3C37 115.55%), #073833'
              },
              {
                name: 'Vanilla',
                prettyName: 'Vanilla',
                description: 'A rich vanilla taste with a smooth texture.',
                link: '/buy-online/vype-epod-vpro-cartridges/epod-nicotine-salts-vanilla-medley/',
                price: '$13.00',
                slug: 'vanilla',
                background: 'linear-gradient(110.85deg, #CA9957 -7.61%, #725628 113.2%), #9B7438'
              },
              {
                name: 'Strawberry',
                prettyName: 'Strawberry',
                description: 'A fusion of strawberry with green fruit notes.',
                link: '/buy-online/vype-epod-vpro-cartridges/garden-strawberry/',
                price: '$13.00',
                slug: 'strawberry',
                background: 'linear-gradient(110.85deg, #DF525C -7.61%, #A4101C 115.55%), #DD0C34'
              }
            ]}
          />
          <FlavourShowcase
            handleCtaClick={() => setStep(2)}
            ctaLabel="PERSONALIZE YOUR DEVICE"
            flavours={epodFlavours}
            hideCurrentFlavour={hideCurrentFlavour}
            isEpod
          />
        </Slide>
        <Slide>
          <CustomizeCarousel
            navigation={[
              {
                label: 'FLAVOURS',
                icon: 'pods',
                onClick: () => setStep(1)
              },
              {
                label: 'ePod',
                icon: 'epod',
                onClick: () => setStep(0)
              }
            ]}
          />
          <CustomizeEpod handleCtaClick={() => setStep(0)} />
        </Slide>
      </Inner>
      <Dots as={ScrollAnimation} animateIn="fadeInUp" offset={0} delay={1200} animateOnce>
        <Dot onClick={() => setStep(0)} active={step === 0} />
        <Dot onClick={() => setStep(1)} active={step === 1} />
        <Dot onClick={() => setStep(2)} active={step === 2} />
      </Dots>
    </Container>
  )
}

EpodSlideshow.propTypes = {
  steps: number.isRequired
}

export default EpodSlideshow
